import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  Flame,
  Trophy,
  Target,
  Droplets,
  Moon,
  Play,
  AlertTriangle,
  LogOut,
  User,
  BarChart3,
  Users,
  Plus,
  Minus,
  Menu,
  X,
  Quote,
  Award,
  Gift,
  Share2,
  RefreshCw,
  Medal,
  Heart,
  Brain,
  Zap,
  Lock,
  Crown,
  Swords
} from "lucide-react";
import SubscriptionPaywall from "../components/SubscriptionPaywall";
import FeatureLock from "../components/FeatureLock";
import CardRequiredModal from "../components/CardRequiredModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_3e64acab-2561-42e6-b860-06b1a563df4d/artifacts/5nvw3yv2_6ECA6BBA-F429-4238-A743-EE6BF2D1D33F.png";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout, fetchUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [dailyTracking, setDailyTracking] = useState({ water_glasses: 0, sleep_hours: 0, workout_completed: false });
  const [quote, setQuote] = useState("");
  const [coachMessage, setCoachMessage] = useState("");
  const [badgeData, setBadgeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showQuitModal, setShowQuitModal] = useState(false);
  const [quitMessage, setQuitMessage] = useState("");
  const [quitData, setQuitData] = useState(null);
  const [todayMood, setTodayMood] = useState(null);
  const [currentChallenge, setCurrentChallenge] = useState(null);
  const [personalGoals, setPersonalGoals] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showCardRequired, setShowCardRequired] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    fetchSubscriptionStatus();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, trackingRes, quoteRes, coachRes, badgeRes, moodRes, challengeRes, goalsRes] = await Promise.all([
        axios.get(`${API}/profile/stats`),
        axios.get(`${API}/tracking/daily`),
        axios.get(`${API}/coaching/quote`),
        axios.post(`${API}/coaching/message`, { context: "daily_motivation" }),
        axios.get(`${API}/badges/current`),
        axios.get(`${API}/mood/today`).catch(() => ({ data: { checkin: null } })),
        axios.get(`${API}/challenges/current`).catch(() => ({ data: { challenge: null } })),
        axios.get(`${API}/goals/personal`).catch(() => ({ data: { goals: null } }))
      ]);
      
      setStats(statsRes.data);
      setDailyTracking(trackingRes.data.tracking);
      setQuote(quoteRes.data.quote);
      setCoachMessage(coachRes.data.message);
      setBadgeData(badgeRes.data);
      setTodayMood(moodRes.data.checkin);
      setCurrentChallenge(challengeRes.data.challenge);
      setPersonalGoals(goalsRes.data.goals);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`);
      setSubscriptionStatus(response.data);
      
      // Show card required modal if no payment method
      if (response.data.requires_payment_method) {
        setShowCardRequired(true);
      }
      // Show paywall if trial expired (and has card)
      else if (response.data.trial_expired) {
        setShowPaywall(true);
      }
    } catch (error) {
      console.error("Failed to fetch subscription status:", error);
    }
  };

  const updateWater = async (delta) => {
    const newValue = Math.max(0, dailyTracking.water_glasses + delta);
    setDailyTracking(prev => ({ ...prev, water_glasses: newValue }));
    
    try {
      await axios.post(`${API}/tracking/daily`, { ...dailyTracking, water_glasses: newValue });
    } catch (error) {
      toast.error("Failed to update water tracking");
    }
  };

  const updateSleep = async (delta) => {
    const newValue = Math.max(0, Math.min(24, dailyTracking.sleep_hours + delta));
    setDailyTracking(prev => ({ ...prev, sleep_hours: newValue }));
    
    try {
      await axios.post(`${API}/tracking/daily`, { ...dailyTracking, sleep_hours: newValue });
    } catch (error) {
      toast.error("Failed to update sleep tracking");
    }
  };

  const handleQuitFeeling = async () => {
    try {
      const response = await axios.post(`${API}/coaching/quit-recovery-full`);
      setQuitMessage(response.data.message);
      setQuitData(response.data);
      setShowQuitModal(true);
    } catch (error) {
      toast.error("Couldn't get recovery message");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary animate-pulse text-2xl font-bold">LOADING...</div>
      </div>
    );
  }

  const completionPercentage = stats?.completion_percentage || 0;
  const currentDay = user?.current_day || 1;
  const streak = user?.streak || 0;

  // MANDATORY CARD CHECK - Block all access until card is added
  if (showCardRequired) {
    return (
      <div className="min-h-screen bg-background">
        <CardRequiredModal />
      </div>
    );
  }

  // PAYWALL CHECK - Block access if trial expired
  if (showPaywall) {
    return (
      <div className="min-h-screen bg-background">
        <SubscriptionPaywall 
          trialDaysLeft={subscriptionStatus?.trial_days_left || 0}
          trialExpired={subscriptionStatus?.trial_expired || false}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Trial Banner */}
      {subscriptionStatus?.is_trial && subscriptionStatus?.has_payment_method && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-primary to-orange-500 text-white py-2 px-4 text-center">
          <span className="text-sm font-bold">
            🔥 FREE TRIAL: {subscriptionStatus.trial_days_left} day{subscriptionStatus.trial_days_left !== 1 ? 's' : ''} left • 
            <Link to="/pricing" className="underline ml-2 hover:text-white/80">
              Upgrade Now
            </Link>
          </span>
        </div>
      )}
      
      {/* Mobile Menu */}
      <div className={`fixed inset-0 z-50 bg-background/95 backdrop-blur-lg transition-transform duration-300 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} lg:hidden`}>
        <div className="flex flex-col h-full p-6">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Coach Coop" className="h-10 w-10 rounded-sm" />
              <span className="font-bold tracking-tight">COACH COOP</span>
            </div>
            <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(false)}>
              <X className="h-6 w-6" />
            </Button>
          </div>
          
          <nav className="flex flex-col gap-4">
            <Link to="/quitter-mode" className="flex items-center gap-3 p-3 bg-primary/10 rounded-sm text-primary border border-primary/30">
              <Zap className="h-5 w-5" /> Focus Mode
            </Link>
            <Link to="/dashboard" className="flex items-center gap-3 p-3 bg-secondary rounded-sm">
              <Target className="h-5 w-5" /> Dashboard
            </Link>
            <Link to={`/workout/${currentDay}`} className="flex items-center gap-3 p-3 hover:bg-secondary rounded-sm">
              <Play className="h-5 w-5" /> Today's Workout
            </Link>
            <Link to="/progress" className="flex items-center gap-3 p-3 hover:bg-secondary rounded-sm">
              <BarChart3 className="h-5 w-5" /> Progress
            </Link>
            <Link to="/leaderboard" className="flex items-center gap-3 p-3 hover:bg-secondary rounded-sm">
              <Users className="h-5 w-5" /> Leaderboard
            </Link>
            <Link to="/achievements" className="flex items-center gap-3 p-3 hover:bg-secondary rounded-sm">
              <Award className="h-5 w-5" /> Achievements
            </Link>
            <Link to="/referrals" className="flex items-center gap-3 p-3 hover:bg-secondary rounded-sm">
              <Gift className="h-5 w-5" /> Referrals
            </Link>
            <Link to="/share" className="flex items-center gap-3 p-3 hover:bg-secondary rounded-sm">
              <Share2 className="h-5 w-5" /> Share Progress
            </Link>
            <Link to="/profile" className="flex items-center gap-3 p-3 hover:bg-secondary rounded-sm">
              <User className="h-5 w-5" /> Profile
            </Link>
          </nav>
          
          <div className="mt-auto">
            <Button variant="outline" onClick={handleLogout} className="w-full uppercase font-bold tracking-widest rounded-sm">
              <LogOut className="mr-2 h-4 w-4" /> Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileMenuOpen(true)}>
                <Menu className="h-6 w-6" />
              </Button>
              <img src={LOGO_URL} alt="Coach Coop" className="h-8 w-8 rounded-sm" />
              <span className="font-bold tracking-tight hidden sm:inline">COACH COOP</span>
            </div>
            
            <nav className="hidden lg:flex items-center gap-6">
              <Link to="/dashboard" className="text-primary font-semibold uppercase text-sm tracking-wider">Dashboard</Link>
              <Link to={`/workout/${currentDay}`} className="text-muted-foreground hover:text-foreground uppercase text-sm tracking-wider">Workout</Link>
              <Link to="/progress" className="text-muted-foreground hover:text-foreground uppercase text-sm tracking-wider">Progress</Link>
              <Link to="/leaderboard" className="text-muted-foreground hover:text-foreground uppercase text-sm tracking-wider">Leaderboard</Link>
              <Link to="/achievements" className="text-muted-foreground hover:text-foreground uppercase text-sm tracking-wider">Badges</Link>
            </nav>
            
            <div className="flex items-center gap-4">
              <Link to="/quitter-mode">
                <Button variant="outline" size="sm" className="hidden lg:flex border-primary text-primary hover:bg-primary hover:text-white">
                  <Zap className="h-4 w-4 mr-1" />
                  Focus Mode
                </Button>
              </Link>
              <Link to="/profile">
                <Button variant="ghost" size="icon" className="hidden lg:flex">
                  <User className="h-5 w-5" />
                </Button>
              </Link>
              <Button variant="ghost" size="icon" onClick={handleLogout} className="hidden lg:flex">
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Personal Why Banner */}
        {user?.personal_why && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-4 bg-primary/10 border border-primary/30 rounded-sm"
          >
            <div className="flex items-start gap-3">
              <Quote className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
              <div>
                <div className="text-sm text-primary uppercase font-bold tracking-wider mb-1">Your Why</div>
                <div className="text-foreground">{user.personal_why}</div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground uppercase tracking-wider">Day</span>
                  <Target className="h-4 w-4 text-primary" />
                </div>
                <div className="text-4xl font-black text-primary font-mono">{currentDay}</div>
                <div className="text-sm text-muted-foreground">of 60</div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground uppercase tracking-wider">Streak</span>
                  <Flame className="h-4 w-4 text-orange-500" />
                </div>
                <div className="text-4xl font-black text-orange-500 font-mono">{streak}</div>
                <div className="text-sm text-muted-foreground">days</div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground uppercase tracking-wider">Progress</span>
                  <Trophy className="h-4 w-4 text-accent" />
                </div>
                <div className="text-4xl font-black text-accent font-mono">{Math.round(completionPercentage)}%</div>
                <Progress value={completionPercentage} className="h-1 mt-2" />
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground uppercase tracking-wider">Workouts</span>
                  <BarChart3 className="h-4 w-4 text-blue-500" />
                </div>
                <div className="text-4xl font-black text-blue-500 font-mono">{stats?.total_workouts || 0}</div>
                <div className="text-sm text-muted-foreground">completed</div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Bento Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Today's Workout - Large */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.5 }}
            className="lg:col-span-2"
          >
            <Card className="bg-card border-border h-full">
              <CardHeader>
                <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
                  <Play className="h-5 w-5 text-primary" />
                  Today's Workout - Day {currentDay}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="aspect-video bg-secondary rounded-sm flex items-center justify-center mb-4 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent" />
                  <Button
                    data-testid="start-workout-btn"
                    size="lg"
                    onClick={() => navigate(`/workout/${currentDay}`)}
                    className="bg-primary hover:bg-primary/90 uppercase font-bold tracking-widest rounded-sm z-10"
                  >
                    <Play className="mr-2 h-5 w-5" /> Start Workout
                  </Button>
                </div>
                
                {coachMessage && (
                  <div className="p-4 bg-secondary/50 rounded-sm border border-border">
                    <div className="text-sm text-primary uppercase font-bold tracking-wider mb-2">Coach Says:</div>
                    <p className="text-foreground italic">"{coachMessage}"</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Tracking */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.6 }}
            className="space-y-6"
          >
            {/* Water Tracking */}
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Droplets className="h-5 w-5 text-blue-400" />
                    <span className="font-bold uppercase tracking-tight">Water</span>
                  </div>
                  <span className="text-2xl font-mono font-bold text-blue-400">{dailyTracking.water_glasses}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    data-testid="water-minus-btn"
                    variant="outline"
                    size="icon"
                    onClick={() => updateWater(-1)}
                    className="rounded-sm"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <div className="flex-1 flex gap-1">
                    {[...Array(8)].map((_, i) => (
                      <div
                        key={i}
                        className={`flex-1 h-6 rounded-sm transition-colors ${
                          i < dailyTracking.water_glasses ? 'bg-blue-400' : 'bg-secondary'
                        }`}
                      />
                    ))}
                  </div>
                  <Button
                    data-testid="water-plus-btn"
                    variant="outline"
                    size="icon"
                    onClick={() => updateWater(1)}
                    className="rounded-sm"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="text-xs text-muted-foreground mt-2 text-center">glasses today</div>
              </CardContent>
            </Card>

            {/* Sleep Tracking */}
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Moon className="h-5 w-5 text-purple-400" />
                    <span className="font-bold uppercase tracking-tight">Sleep</span>
                  </div>
                  <span className="text-2xl font-mono font-bold text-purple-400">{dailyTracking.sleep_hours}h</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    data-testid="sleep-minus-btn"
                    variant="outline"
                    size="icon"
                    onClick={() => updateSleep(-0.5)}
                    className="rounded-sm"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <Progress value={(dailyTracking.sleep_hours / 10) * 100} className="flex-1 h-6" />
                  <Button
                    data-testid="sleep-plus-btn"
                    variant="outline"
                    size="icon"
                    onClick={() => updateSleep(0.5)}
                    className="rounded-sm"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="text-xs text-muted-foreground mt-2 text-center">hours last night</div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Daily Quote */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.7 }}
            className="lg:col-span-2"
          >
            <Card className="bg-gradient-to-br from-primary/10 to-transparent border-primary/30">
              <CardContent className="p-6">
                <Quote className="h-8 w-8 text-primary mb-4" />
                <p className="text-xl font-medium italic text-foreground mb-4">"{quote}"</p>
                <div className="text-sm text-muted-foreground uppercase tracking-wider">Daily Motivation</div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Quit Recovery Button */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.8 }}
          >
            <Card className="bg-card border-border h-full">
              <CardContent className="p-6 flex flex-col justify-center h-full">
                <Button
                  data-testid="quit-recovery-btn"
                  variant="outline"
                  onClick={handleQuitFeeling}
                  className="w-full border-2 border-orange-500/50 text-orange-500 hover:bg-orange-500 hover:text-white uppercase font-bold tracking-widest rounded-sm py-6"
                >
                  <AlertTriangle className="mr-2 h-5 w-5" />
                  I Feel Like Quitting
                </Button>
                <p className="text-xs text-muted-foreground mt-4 text-center">
                  Tap when motivation is low. Coach Coop's got your back.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Quick Actions Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          {/* Mood Check-In - Pro Feature */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}>
            {subscriptionStatus?.features_available?.mood_tracking ? (
              <Link to="/mood">
                <Card className={`bg-card border-border hover:border-primary/50 transition-all cursor-pointer group ${!todayMood ? 'ring-2 ring-primary/50 animate-pulse' : ''}`}>
                  <CardContent className="p-4 text-center">
                    <div className="w-12 h-12 mx-auto rounded-full bg-primary/20 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                      <Brain className="h-6 w-6 text-primary" />
                    </div>
                    <div className="font-bold text-sm uppercase tracking-tight">
                      {todayMood ? 'Mood Logged' : 'Check-In'}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {todayMood ? `Energy: ${todayMood.energy_level}/10` : 'Log your mood'}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ) : (
              <Link to="/pricing">
                <Card className="bg-card border-border hover:border-yellow-500/50 transition-all cursor-pointer group relative overflow-hidden">
                  <div className="absolute top-2 right-2">
                    <div className="px-2 py-0.5 bg-yellow-500/20 rounded-full flex items-center gap-1">
                      <Lock className="h-3 w-3 text-yellow-400" />
                      <span className="text-[10px] text-yellow-400 font-bold">PRO</span>
                    </div>
                  </div>
                  <CardContent className="p-4 text-center opacity-60">
                    <div className="w-12 h-12 mx-auto rounded-full bg-primary/20 flex items-center justify-center mb-2">
                      <Brain className="h-6 w-6 text-primary" />
                    </div>
                    <div className="font-bold text-sm uppercase tracking-tight">Mood Check-In</div>
                    <div className="text-xs text-muted-foreground">Upgrade to unlock</div>
                  </CardContent>
                </Card>
              </Link>
            )}
          </motion.div>
          
          {/* Weekly Challenge - Pro Feature */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.0 }}>
            {subscriptionStatus?.features_available?.weekly_challenges ? (
              <Link to="/challenges">
                <Card className="bg-card border-border hover:border-accent/50 transition-all cursor-pointer group">
                  <CardContent className="p-4 text-center">
                    <div className="w-12 h-12 mx-auto rounded-full bg-accent/20 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                      <Zap className="h-6 w-6 text-accent" />
                    </div>
                    <div className="font-bold text-sm uppercase tracking-tight">Challenge</div>
                    <div className="text-xs text-muted-foreground">
                      {currentChallenge?.completed ? 'Complete!' : currentChallenge?.title?.slice(0, 15) + '...' || 'View challenge'}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ) : (
              <Link to="/pricing">
                <Card className="bg-card border-border hover:border-yellow-500/50 transition-all cursor-pointer group relative overflow-hidden">
                  <div className="absolute top-2 right-2">
                    <div className="px-2 py-0.5 bg-yellow-500/20 rounded-full flex items-center gap-1">
                      <Lock className="h-3 w-3 text-yellow-400" />
                      <span className="text-[10px] text-yellow-400 font-bold">PRO</span>
                    </div>
                  </div>
                  <CardContent className="p-4 text-center opacity-60">
                    <div className="w-12 h-12 mx-auto rounded-full bg-accent/20 flex items-center justify-center mb-2">
                      <Zap className="h-6 w-6 text-accent" />
                    </div>
                    <div className="font-bold text-sm uppercase tracking-tight">Challenges</div>
                    <div className="text-xs text-muted-foreground">Upgrade to unlock</div>
                  </CardContent>
                </Card>
              </Link>
            )}
          </motion.div>
          
          {/* Personal Goals - Pro Feature */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1 }}>
            {subscriptionStatus?.features_available?.personal_goals ? (
              <Link to="/goals">
                <Card className={`bg-card border-border hover:border-pink-500/50 transition-all cursor-pointer group ${!personalGoals?.why_started ? 'ring-2 ring-pink-500/50' : ''}`}>
                  <CardContent className="p-4 text-center">
                    <div className="w-12 h-12 mx-auto rounded-full bg-pink-500/20 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                      <Heart className="h-6 w-6 text-pink-500" />
                    </div>
                    <div className="font-bold text-sm uppercase tracking-tight">
                      {personalGoals?.why_started ? 'My Why' : 'Set Goals'}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {personalGoals?.doing_it_for ? `For: ${personalGoals.doing_it_for}` : 'Define your why'}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ) : (
              <Link to="/pricing">
                <Card className="bg-card border-border hover:border-yellow-500/50 transition-all cursor-pointer group relative overflow-hidden">
                  <div className="absolute top-2 right-2">
                    <div className="px-2 py-0.5 bg-yellow-500/20 rounded-full flex items-center gap-1">
                      <Lock className="h-3 w-3 text-yellow-400" />
                      <span className="text-[10px] text-yellow-400 font-bold">PRO</span>
                    </div>
                  </div>
                  <CardContent className="p-4 text-center opacity-60">
                    <div className="w-12 h-12 mx-auto rounded-full bg-pink-500/20 flex items-center justify-center mb-2">
                      <Heart className="h-6 w-6 text-pink-500" />
                    </div>
                    <div className="font-bold text-sm uppercase tracking-tight">Personal Goals</div>
                    <div className="text-xs text-muted-foreground">Upgrade to unlock</div>
                  </CardContent>
                </Card>
              </Link>
            )}
          </motion.div>
          
          {/* Achievements - Basic Feature */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2 }}>
            {subscriptionStatus?.features_available?.achievements ? (
              <Link to="/achievements">
                <Card className="bg-card border-border hover:border-primary/50 transition-all cursor-pointer group">
                  <CardContent className="p-4 text-center">
                    <div 
                      className="w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-2 group-hover:scale-110 transition-transform"
                      style={{ 
                        backgroundColor: badgeData?.current_badge?.color ? `${badgeData.current_badge.color}20` : '#333',
                        border: `2px solid ${badgeData?.current_badge?.color || '#666'}`
                      }}
                    >
                      <Medal className="h-6 w-6" style={{ color: badgeData?.current_badge?.color || '#666' }} />
                    </div>
                    <div className="font-bold text-sm uppercase tracking-tight">{badgeData?.current_badge?.name || 'Starter'}</div>
                    <div className="text-xs text-muted-foreground">Score: {badgeData?.discipline_score || 0}/100</div>
                  </CardContent>
                </Card>
              </Link>
            ) : (
              <Link to="/pricing">
                <Card className="bg-card border-border hover:border-green-500/50 transition-all cursor-pointer group relative overflow-hidden">
                  <div className="absolute top-2 right-2">
                    <div className="px-2 py-0.5 bg-green-500/20 rounded-full flex items-center gap-1">
                      <Lock className="h-3 w-3 text-green-400" />
                      <span className="text-[10px] text-green-400 font-bold">BASIC</span>
                    </div>
                  </div>
                  <CardContent className="p-4 text-center opacity-60">
                    <div className="w-12 h-12 mx-auto rounded-full bg-orange-500/20 flex items-center justify-center mb-2">
                      <Medal className="h-6 w-6 text-orange-500" />
                    </div>
                    <div className="font-bold text-sm uppercase tracking-tight">Achievements</div>
                    <div className="text-xs text-muted-foreground">Upgrade to unlock</div>
                  </CardContent>
                </Card>
              </Link>
            )}
          </motion.div>
        </div>
        
        {/* Second Row Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          {/* Partner Workouts - Elite Feature */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3 }}>
            {subscriptionStatus?.features_available?.partner_workouts ? (
              <Link to="/partner-workouts" data-testid="partner-workouts-link">
                <Card className="bg-card border-border hover:border-pink-500/50 transition-all cursor-pointer group">
                  <CardContent className="p-4 text-center">
                    <div className="w-12 h-12 mx-auto rounded-full bg-pink-500/20 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                      <Heart className="h-6 w-6 text-pink-500" />
                    </div>
                    <div className="font-bold text-sm uppercase tracking-tight">Partner Workouts</div>
                    <div className="text-xs text-muted-foreground">Train together</div>
                  </CardContent>
                </Card>
              </Link>
            ) : (
              <Link to="/pricing">
                <Card className="bg-card border-border hover:border-purple-500/50 transition-all cursor-pointer group relative overflow-hidden">
                  <div className="absolute top-2 right-2">
                    <div className="px-2 py-0.5 bg-purple-500/20 rounded-full flex items-center gap-1">
                      <Crown className="h-3 w-3 text-purple-400" />
                      <span className="text-[10px] text-purple-400 font-bold">ELITE</span>
                    </div>
                  </div>
                  <CardContent className="p-4 text-center opacity-60">
                    <div className="w-12 h-12 mx-auto rounded-full bg-pink-500/20 flex items-center justify-center mb-2">
                      <Heart className="h-6 w-6 text-pink-500" />
                    </div>
                    <div className="font-bold text-sm uppercase tracking-tight">Partner Workouts</div>
                    <div className="text-xs text-muted-foreground">Upgrade to unlock</div>
                  </CardContent>
                </Card>
              </Link>
            )}
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.4 }}>
            {/* Partner Challenges - Elite Feature */}
            {subscriptionStatus?.features_available?.partner_workouts ? (
              <Link to="/partner-challenges" data-testid="partner-challenges-link">
                <Card className="bg-card border-border hover:border-red-500/50 transition-all cursor-pointer group">
                  <CardContent className="p-4 text-center">
                    <div className="w-12 h-12 mx-auto rounded-full bg-red-500/20 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                      <Swords className="h-6 w-6 text-red-500" />
                    </div>
                    <div className="font-bold text-sm uppercase tracking-tight">Challenges</div>
                    <div className="text-xs text-muted-foreground">Battle your partner</div>
                  </CardContent>
                </Card>
              </Link>
            ) : (
              <Link to="/pricing">
                <Card className="bg-card border-border hover:border-purple-500/50 transition-all cursor-pointer group relative overflow-hidden">
                  <div className="absolute top-2 right-2">
                    <div className="px-2 py-0.5 bg-purple-500/20 rounded-full flex items-center gap-1">
                      <Crown className="h-3 w-3 text-purple-400" />
                      <span className="text-[10px] text-purple-400 font-bold">ELITE</span>
                    </div>
                  </div>
                  <CardContent className="p-4 text-center opacity-60">
                    <div className="w-12 h-12 mx-auto rounded-full bg-red-500/20 flex items-center justify-center mb-2">
                      <Swords className="h-6 w-6 text-red-500" />
                    </div>
                    <div className="font-bold text-sm uppercase tracking-tight">Challenges</div>
                    <div className="text-xs text-muted-foreground">Upgrade to unlock</div>
                  </CardContent>
                </Card>
              </Link>
            )}
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5 }}>
            <Link to="/referrals">
              <Card className="bg-card border-border hover:border-accent/50 transition-all cursor-pointer group">
                <CardContent className="p-4 text-center">
                  <div className="w-12 h-12 mx-auto rounded-full bg-accent/20 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                    <Gift className="h-6 w-6 text-accent" />
                  </div>
                  <div className="font-bold text-sm uppercase tracking-tight">Referrals</div>
                  <div className="text-xs text-muted-foreground">Invite friends</div>
                </CardContent>
              </Card>
            </Link>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.6 }}>
            <Link to="/share">
              <Card className="bg-card border-border hover:border-blue-500/50 transition-all cursor-pointer group">
                <CardContent className="p-4 text-center">
                  <div className="w-12 h-12 mx-auto rounded-full bg-blue-500/20 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                    <Share2 className="h-6 w-6 text-blue-500" />
                  </div>
                  <div className="font-bold text-sm uppercase tracking-tight">Share</div>
                  <div className="text-xs text-muted-foreground">Show progress</div>
                </CardContent>
              </Card>
            </Link>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.7 }}>
            <Link to="/cycle">
              <Card className="bg-card border-border hover:border-purple-500/50 transition-all cursor-pointer group">
                <CardContent className="p-4 text-center">
                  <div className="w-12 h-12 mx-auto rounded-full bg-purple-500/20 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                    <RefreshCw className="h-6 w-6 text-purple-500" />
                  </div>
                  <div className="font-bold text-sm uppercase tracking-tight">Cycle</div>
                  <div className="text-xs text-muted-foreground">Reset & reflect</div>
                </CardContent>
              </Card>
            </Link>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.8 }}>
            {subscriptionStatus?.features_available?.leaderboard ? (
              <Link to="/leaderboard">
                <Card className="bg-card border-border hover:border-yellow-500/50 transition-all cursor-pointer group">
                  <CardContent className="p-4 text-center">
                    <div className="w-12 h-12 mx-auto rounded-full bg-yellow-500/20 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                      <Users className="h-6 w-6 text-yellow-500" />
                    </div>
                    <div className="font-bold text-sm uppercase tracking-tight">Leaderboard</div>
                    <div className="text-xs text-muted-foreground">See rankings</div>
                  </CardContent>
                </Card>
              </Link>
            ) : (
              <Link to="/pricing">
                <Card className="bg-card border-border hover:border-green-500/50 transition-all cursor-pointer group relative overflow-hidden">
                  <div className="absolute top-2 right-2">
                    <div className="px-2 py-0.5 bg-green-500/20 rounded-full flex items-center gap-1">
                      <Lock className="h-3 w-3 text-green-400" />
                      <span className="text-[10px] text-green-400 font-bold">BASIC</span>
                    </div>
                  </div>
                  <CardContent className="p-4 text-center opacity-60">
                    <div className="w-12 h-12 mx-auto rounded-full bg-yellow-500/20 flex items-center justify-center mb-2">
                      <Users className="h-6 w-6 text-yellow-500" />
                    </div>
                    <div className="font-bold text-sm uppercase tracking-tight">Leaderboard</div>
                    <div className="text-xs text-muted-foreground">Upgrade to unlock</div>
                  </CardContent>
                </Card>
              </Link>
            )}
          </motion.div>
        </div>
      </main>

      {/* Quit Recovery Modal */}
      {showQuitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-lg"
          >
            <Card className="bg-card border-primary">
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Flame className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-2xl font-black uppercase tracking-tighter mb-2">DON'T QUIT</h3>
                  {quitData?.doing_it_for_text && (
                    <p className="text-sm text-accent uppercase tracking-wider">
                      Remember: You're doing this for {quitData.doing_it_for_text}
                    </p>
                  )}
                </div>
                
                <p className="text-lg text-foreground mb-6 text-center italic">"{quitMessage}"</p>
                
                {/* Recent Wins Reminder */}
                {quitData?.recent_wins?.length > 0 && (
                  <div className="mb-6 p-4 bg-accent/10 border border-accent/30 rounded-sm">
                    <p className="text-sm text-center mb-2 text-muted-foreground uppercase tracking-wider">You've already noticed:</p>
                    <div className="flex justify-center gap-2 flex-wrap">
                      {quitData.recent_wins.map((win, i) => (
                        <span key={i} className="px-3 py-1 bg-accent/20 rounded-full text-xs uppercase font-bold text-accent">
                          {win.replace('_', ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="space-y-3">
                  <Button
                    data-testid="quit-modal-continue-btn"
                    onClick={() => {
                      setShowQuitModal(false);
                      navigate(`/workout/${currentDay}`);
                    }}
                    className="w-full bg-primary hover:bg-primary/90 uppercase font-bold tracking-widest rounded-sm"
                  >
                    I'm Ready - Let's Go
                  </Button>
                  
                  {quitData?.reduced_workout_available && (
                    <Button
                      data-testid="quit-modal-reduced-btn"
                      variant="outline"
                      onClick={() => {
                        setShowQuitModal(false);
                        navigate(`/workout/${currentDay}?reduced=true`);
                        toast.success("Starting 10-minute workout. You've got this!");
                      }}
                      className="w-full border-accent text-accent hover:bg-accent hover:text-black uppercase font-bold tracking-widest rounded-sm"
                    >
                      Just 10 Minutes - I Can Do That
                    </Button>
                  )}
                  
                  <Button
                    variant="ghost"
                    onClick={() => setShowQuitModal(false)}
                    className="w-full text-muted-foreground uppercase text-sm"
                  >
                    I Need More Time
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      )}
    </div>
  );
}
